/**
 * PaneRail — the way back to a pane you closed.
 *
 * Every floating pane has a close button and, until this existed, nothing
 * undid it: the pane went away, its slot stayed `visible: false` in
 * localStorage, and the only recovery was clearing site data. A close that
 * cannot be reversed is a trap, not an affordance.
 *
 * So the rail is not decoration — it is the other half of the close button.
 * It shows every pane the surface HAS, not every pane currently open, and
 * an inactive pill is the discoverable "bring that back".
 *
 * It is shared rather than per-surface for the reason `usePanelDrag` is:
 * review and tables grew the same control twice and it drifted. Callers pass
 * their own ids and labels; the rail owns the treatment.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { UsePanelSurfaceResult } from './usePanelSurface';

export interface PaneRailItem<Id extends string> {
  id: Id;
  label: string;
  icon?: React.ReactNode;
  /**
   * How much is in there. Shown as a small tally on the pill so a closed
   * pane can still say it has something — zero renders nothing, because a
   * badge reading "0" is noise, not information.
   */
  badge?: number;
}

interface Props<Id extends string> {
  panels: UsePanelSurfaceResult<Id>;
  items: PaneRailItem<Id>[];
  /** Announced to screen readers; the rail itself carries no visible title. */
  ariaLabel?: string;
  /**
   * How much room the labels get.
   *
   * `full` (default) always shows them. `auto` drops to icons below `xl`,
   * for a rail that shares the top bar with everything else and must not
   * push it into overflow on a laptop. The label stays in the tooltip and
   * the accessible name either way, so nothing is lost — only the text.
   */
  labels?: 'full' | 'auto' | 'icon';
  className?: string;
}

function PaneRail<Id extends string>({
  panels,
  items,
  ariaLabel,
  labels = 'full',
  className,
}: Props<Id>) {
  const { t } = useTranslation();
  // Nothing floats on a narrow viewport — panes are sheets there, and a rail
  // of toggles for a layout that does not exist would only mislead.
  if (panels.singleColumn || items.length === 0) return null;

  return (
    <div
      role="group"
      aria-label={ariaLabel ?? t('Panels')}
      data-testid="pane-rail"
      className={clsx(
        'glass-bar inline-flex items-center gap-0.5 rounded-full p-0.5',
        'border border-hairline-soft shadow-sm',
        className
      )}
    >
      {items.map((item) => {
        const visible = panels.layout.slots[item.id]?.visible ?? false;
        return (
          <button
            key={item.id}
            type="button"
            data-testid={`pane-toggle-${item.id}`}
            data-active={visible}
            aria-pressed={visible}
            aria-label={item.label}
            title={item.label}
            onClick={() => {
              // Reopening also raises it: a pane restored UNDER another one
              // reads as a toggle that did nothing.
              panels.setVisible(item.id, !visible);
              if (!visible) panels.focus(item.id);
            }}
            className={clsx(
              'nav-pill flex items-center gap-1.5 rounded-full px-2.5 py-1',
              'text-[11px] font-medium transition-colors duration-200 ease-slide',
              visible
                ? 'nav-pill-active text-fg-strong'
                : 'text-fg-muted hover:text-fg-body'
            )}
          >
            {item.icon && <span className="w-3.5 h-3.5 flex items-center">{item.icon}</span>}
            {labels !== 'icon' && (
              <span className={labels === 'auto' ? 'hidden xl:inline' : undefined}>
                {item.label}
              </span>
            )}
            {!!item.badge && (
              <span className="text-[10px] tabular-nums text-fg-muted">
                {item.badge}
              </span>
            )}
            {/* The champagne dot is the only accent: small, and only ever
                marks the open state. */}
            <span
              className={clsx(
                'w-1 h-1 rounded-full transition-opacity duration-200',
                labels === 'icon' && 'hidden',
                labels === 'auto' && 'hidden xl:block',
                visible ? 'bg-champagne opacity-100' : 'opacity-0'
              )}
            />
          </button>
        );
      })}
    </div>
  );
}

export default PaneRail;
